import { ReactNode } from 'react'
import { generatePath, Link as RouterLink } from 'react-router-dom'
import Link from '@mui/material/Link'
import { getClientLifecycleStage } from '@common/helpers/clientLifecycle'
import { addHttp } from '@common/helpers/helpers'
import { CapitalRaiseInvestorKind, ReferralCounterpartyKind } from '@common/interfaces/overview'
import { PhotoSubjectType } from '@common/interfaces/photo'
import { ROUTES } from '../../constants/routes'
import { getBoxFileId } from '../../helpers/boxFileLink'
import BoxFileLink from '../Common/BoxFileLink'

// Re-exported for the referral cells; the server now merges both sides of a
// referral into this shape (with a Side column), so the type lives in common.
export type { IReferralRowWithSide } from '@common/interfaces/overview'

export const personHref = (id: string) => generatePath(ROUTES.CRM_PERSON_PAGE, { id })
export const orgHref = (id: string) =>
  `${generatePath(ROUTES.ORGANIZATION_PAGE, { id })}?source=organization`
export const companyHref = (id: string) => generatePath(ROUTES.CRM_COMPANY_PAGE, { id })
export const campaignHref = (id: string) => generatePath(ROUTES.CRM_CAMPAIGN_PAGE, { id })
export const entityInfoHref = (id: string) => generatePath(ROUTES.ENTITY_PAGE, { id })

/** What a company needs to carry for `companyProfileHref` to route it. */
export interface ICompanyRoutingTarget {
  id: string
  clientInfoId: string | null
  clientStatus: string | null
  // OpsReporting.id backing the prospect profile; null unless the client is a prospect.
  firstOPSReportingId: string | null
}

/**
 * The company's profile page *at its current stage* — a client opens the client
 * page, a company in diligence its diligence page, a prospect its prospect
 * overview, and a pure CRM company (lead / opportunity) the CRM company page.
 *
 * Currently used by the task Company link. Global search
 * (GlobalSearch.helpers.ts), the dashboard agenda row and the meeting-brief modal
 * still carry their own copies of this routing and should move onto this helper —
 * the agenda and brief ones only distinguish client vs CRM company today, so
 * switching them changes where a prospect or diligence company opens.
 * CRMCompanyPage keeps its own `getLifecycleRedirect` on purpose — it asks a
 * different question (redirect away from myself, and to the prospect's Overview
 * tab specifically, since that tab is what this page renders).
 */
export const companyProfileHref = (company: ICompanyRoutingTarget): string => {
  const { id, clientInfoId, clientStatus, firstOPSReportingId } = company
  const stage = clientInfoId ? getClientLifecycleStage(clientStatus) : null

  if (!clientInfoId) {
    return companyHref(id)
  }

  switch (stage) {
    case 'client':
      return generatePath(ROUTES.CLIENT_PAGE, { id: clientInfoId })
    case 'dueDiligence':
      return generatePath(ROUTES.DUE_DILIGENCE, { id: clientInfoId })
    case 'prospect':
      return firstOPSReportingId
        ? generatePath(ROUTES.PROSPECT_PAGE, { id: firstOPSReportingId })
        : companyHref(id)
    default:
      return companyHref(id)
  }
}

export const counterpartyHref = (
  kind: ReferralCounterpartyKind | CapitalRaiseInvestorKind | null,
  id: string | null,
) => {
  if (!id) return null
  if (kind === 'person') return personHref(id)
  if (kind === 'organization') return orgHref(id)
  if (kind === 'company') return companyHref(id)
  // Same destination the Cap Table tab sends an entity to (CapTableSummaryTable),
  // resolved through the same alias -> entity_info chain.
  if (kind === 'entityInfo') return entityInfoHref(id)
  return null
}

/**
 * The photo subject behind a referral counterparty — the avatar counterpart to counterpartyHref.
 *
 * Kept beside it so the two can't drift: a counterparty the rail links through to should show
 * that record's photo, not initials.
 */
export const counterpartySubject = (
  kind: ReferralCounterpartyKind | null,
  id: string | null,
): { subjectType: PhotoSubjectType; id: string } | null => {
  if (!id) return null
  if (kind === 'person') return { subjectType: PhotoSubjectType.People, id }
  if (kind === 'organization') return { subjectType: PhotoSubjectType.Organizations, id }
  if (kind === 'company') return { subjectType: PhotoSubjectType.Company, id }
  return null
}

// `https://www.instagram.com/brand/` reads as `instagram.com/brand`.
export const displayUrl = (url: string) =>
  url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')

/** A stored web address as a new-tab link, shown without its scheme unless `children` says otherwise. */
export const ExternalLink = ({ href, children }: { href: string; children?: ReactNode }) => (
  <Link href={addHttp(href)} target="_blank" rel="noopener noreferrer">
    {children ?? displayUrl(href)}
  </Link>
)

// Salesforce's Instagram field is sometimes a bare handle — and handles can hold periods
// (`brand.official`), so only a scheme, `www.` or the instagram.com host marks a URL.
export const instagramHref = (value: string) =>
  /^(https?:\/\/|www\.)|instagram\.com/i.test(value)
    ? value
    : `instagram.com/${value.replace(/^@/, '')}`

/** A row whose other party is a company or, when it has none, an organization. */
export interface ICompanyOrOrgTarget {
  companyId: string | null
  organizationId: string | null
}

const companyOrOrg = ({ companyId, organizationId }: ICompanyOrOrgTarget) =>
  companyId
    ? { kind: 'company' as const, id: companyId }
    : { kind: 'organization' as const, id: organizationId }

export const companyOrOrgHref = (target: ICompanyOrOrgTarget) => {
  const { kind, id } = companyOrOrg(target)
  return counterpartyHref(kind, id)
}

export const companyOrOrgSubject = (target: ICompanyOrOrgTarget) => {
  const { kind, id } = companyOrOrg(target)
  return counterpartySubject(kind, id)
}

export const companyOrOrgLinkLabel = (target: ICompanyOrOrgTarget) =>
  target.companyId ? 'View company' : 'View organization'

interface INameLinkProps {
  to: string | null
  children: ReactNode
  // Stop the click bubbling to a row-level handler (e.g. the Network detail
  // modal). Off by default for tables with no row click.
  stopPropagation?: boolean
  // Applied to both branches so a caller can truncate the name identically
  // whether or not it resolved to a link.
  className?: string
  // Native hover tooltip — the full value, for when the name is truncated.
  title?: string
}

// An absolute URL points outside the app. A react-router Link would treat it as an
// in-app path and navigate nowhere useful, so it has to be a real anchor.
const isExternalHref = (to: string) => /^https?:\/\//i.test(to)

/**
 * Name cell that links to the entity page, rendering plain text when unlinkable.
 *
 * A Box file (the document behind a capital raise's Source, say) previews in place, and
 * any other external `to` opens in a new tab, so viewing a source never costs the page.
 */
export const NameLink = ({ to, children, stopPropagation, className, title }: INameLinkProps) => {
  if (!to) {
    return (
      <span className={className} title={title}>
        {children}
      </span>
    )
  }

  if (getBoxFileId(to)) {
    return (
      <BoxFileLink link={to} className={className} title={title}>
        {children}
      </BoxFileLink>
    )
  }

  if (isExternalHref(to)) {
    return (
      <Link
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        title={title}
        onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
      >
        {children}
      </Link>
    )
  }

  return (
    <Link
      component={RouterLink}
      to={to}
      className={className}
      title={title}
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
    >
      {children}
    </Link>
  )
}
